import React, { useState } from 'react';
import {
  OPTIONAL_COLUMNS,
  emptyQuestion,
  buildOrSibling,
  connectorLabel,
  sumDirectSubMarks,
  hasMarksMismatch,
  sumQuestionFlat,
  hasOrMismatchAfter,
} from './templateSchema';

/**
 * A single question row rendered as an inline table row.
 *
 * Supports:
 *   - Inline editing of Q.No, Marks, and every visible optional attribute
 *     (optional attributes are rendered as dropdowns populated from Master Values)
 *   - Add / remove sub-questions (recursive QuestionRow)
 *   - "+OR" creates a paired sibling (handled by parent via onAddOrAfter)
 *   - Marks validation: shows a warning if a parent's marks disagree with
 *     the sum of its sub-questions
 *
 * Props:
 *   question, siblings, index, depth (0 = top-level),
 *   visibleColumns, options (dropdown values keyed by column key),
 *   onChange(updated), onDelete(), onAddOrAfter()
 */
function QuestionRow({
  question,
  siblings,
  index,
  depth = 0,
  visibleColumns,
  options = {},
  onChange,
  onDelete,
  onAddOrAfter,
}) {
  const [expanded, setExpanded] = useState(true);
  const hasSubs = (question.subQuestions?.length || 0) > 0;
  const isLastSibling = index === siblings.length - 1;
  const marksMismatch = hasMarksMismatch(question);
  const subsSum = sumDirectSubMarks(question);

  const setField = (field, value) => {
    onChange({ ...question, [field]: value });
  };

  const addSub = () => {
    const sub = emptyQuestion();
    const nextLetter = String.fromCharCode(97 + (question.subQuestions?.length || 0));
    sub.number = `${question.number || index + 1}.${nextLetter}`;
    onChange({
      ...question,
      subQuestions: [...(question.subQuestions || []), sub],
    });
    setExpanded(true);
  };

  const updateSub = (subIndex, updated) => {
    const next = [...(question.subQuestions || [])];
    next[subIndex] = updated;
    onChange({ ...question, subQuestions: next });
  };

  const deleteSub = (subIndex) => {
    const next = [...(question.subQuestions || [])];
    next.splice(subIndex, 1);
    if (subIndex > 0 && next[subIndex - 1]?.connectorAfter && subIndex >= next.length) {
      next[subIndex - 1] = { ...next[subIndex - 1], connectorAfter: null };
    }
    onChange({ ...question, subQuestions: next });
  };

  /**
   * Add an OR-paired sibling to a sub-question at subIndex.
   */
  const addOrAfterSub = (subIndex) => {
    const subs = question.subQuestions || [];
    const source = subs[subIndex];
    const sib = buildOrSibling(source, subIndex);
    const next = [...subs];
    next[subIndex] = {
      ...source,
      connectorAfter: { type: 'OR', label: connectorLabel('OR') },
    };
    next.splice(subIndex + 1, 0, sib);
    onChange({ ...question, subQuestions: next });
    setExpanded(true);
  };

  const visibleCols = OPTIONAL_COLUMNS.filter((c) => visibleColumns.includes(c.key));

  const renderAttrCell = (colKey) => {
    const opts = options?.[colKey];
    if (Array.isArray(opts) && opts.length > 0) {
      return (
        <select
          className="qp-inline-input qp-inline-select"
          value={question[colKey] ?? ''}
          onChange={(e) => setField(colKey, e.target.value)}
        >
          <option value="">—</option>
          {opts.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      );
    }
    // Fallback: free-text when no master values configured
    return (
      <input
        type="text"
        className="qp-inline-input"
        value={question[colKey] ?? ''}
        placeholder="—"
        onChange={(e) => setField(colKey, e.target.value)}
      />
    );
  };

  return (
    <>
      <tr className={`qp-q-row depth-${depth}`}>
        <td className="qp-q-num">
          {hasSubs && (
            <button
              type="button"
              className="qp-q-expand"
              onClick={() => setExpanded((v) => !v)}
              title={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? '▾' : '▸'}
            </button>
          )}
          <span style={{ marginLeft: depth * 14 }}>#{index + 1}</span>
        </td>
        <td>
          <input
            type="text"
            className="qp-inline-input qp-num-input"
            value={question.number}
            placeholder={depth === 0 ? '1' : 'a'}
            onChange={(e) => setField('number', e.target.value)}
          />
        </td>
        <td>
          <div className="qp-marks-wrap">
            <input
              type="number"
              min="0"
              step="0.5"
              className={`qp-inline-input qp-marks-input ${marksMismatch ? 'qp-marks-error' : ''}`}
              value={question.marks ?? 0}
              onChange={(e) =>
                setField('marks', e.target.value === '' ? 0 : Number(e.target.value))
              }
            />
            {marksMismatch && (
              <span
                className="qp-mark-warn"
                title={`Sub-questions total ${subsSum} but parent is ${question.marks}`}
              >
                ⚠
              </span>
            )}
          </div>
        </td>
        {visibleCols.map((col) => (
          <td key={col.key}>{renderAttrCell(col.key)}</td>
        ))}
        <td>
          <div className="qp-actions">
            <button
              type="button"
              className="qp-act-btn qp-act-sub"
              title="Add sub-question"
              onClick={addSub}
            >
              +
            </button>
            {onAddOrAfter && (
              <button
                type="button"
                className="qp-act-btn qp-act-or"
                title="Add OR option (creates a paired sibling)"
                onClick={onAddOrAfter}
              >
                +OR
              </button>
            )}
            <button
              type="button"
              className="qp-act-btn qp-act-del"
              title="Delete"
              onClick={onDelete}
            >
              🗑
            </button>
          </div>
        </td>
      </tr>

      {/* Sub-questions rendered inline under parent */}
      {expanded &&
        (question.subQuestions || []).map((sq, si) => (
          <QuestionRow
            key={sq.id}
            question={sq}
            siblings={question.subQuestions}
            index={si}
            depth={depth + 1}
            visibleColumns={visibleColumns}
            options={options}
            onChange={(u) => updateSub(si, u)}
            onDelete={() => deleteSub(si)}
            onAddOrAfter={() => addOrAfterSub(si)}
          />
        ))}

      {/* Connector label spanning full row */}
      {question.connectorAfter && !isLastSibling && (() => {
        const isOr = question.connectorAfter.type === 'OR';
        const orMismatch = isOr && hasOrMismatchAfter(siblings, index);
        const leftTotal = sumQuestionFlat(question);
        const rightTotal = siblings[index + 1] ? sumQuestionFlat(siblings[index + 1]) : 0;
        return (
          <tr
            className={
              `qp-connector-row qp-connector-${question.connectorAfter.type.toLowerCase()}` +
              (orMismatch ? ' qp-connector-mismatch' : '')
            }
          >
            <td colSpan={3 + visibleCols.length + 1} className="qp-connector-cell">
              <span className="qp-connector-bar">
                <span className="qp-connector-chip">{question.connectorAfter.type}</span>
                <span className="qp-connector-label">
                  {question.connectorAfter.label}
                  {orMismatch && (
                    <span
                      className="qp-connector-warn"
                      title={`OR options must carry equal marks. Left total ${leftTotal}, right total ${rightTotal}.`}
                    >
                      {' '}⚠ Marks mismatch ({leftTotal} ≠ {rightTotal})
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="qp-connector-remove"
                  title="Remove connector"
                  onClick={() => onChange({ ...question, connectorAfter: null })}
                >
                  ×
                </button>
              </span>
            </td>
          </tr>
        );
      })()}
    </>
  );
}

export default QuestionRow;
